import type { ReactNode } from 'react'
import { chakra } from '@chakra-ui/react'
import { Pressable } from '@ayble-health/dashboard-components'
import { Button } from 'components/ui/button'
import { ArrowUpRight, ChevronRight } from './icons'

// C_Card/Stats (Figma — Design System 2.1, node 40005215:9090)
// Spec: docs/design/C_card-stats-design-spec.md

type Trend = 'up' | 'down' | 'flat'

// 30×30 circular trend chip. Only `up` is published in Figma; down/flat fall back
// to the same warm gradient until those variants are designed (spec §6).
function TrendChip({ trend }: { trend?: Trend }) {
  const slot = { width: 30, height: 30, flexShrink: 0, borderRadius: 'var(--radius-pill)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }
  if (!trend) return <span style={{ ...slot, opacity: 0 }} aria-hidden />
  return (
    <span style={{ ...slot, background: 'var(--gradient-data-warm)', color: 'var(--evergreen)' }}>
      <ArrowUpRight />
    </span>
  )
}

// Eyebrow + (right slot: trend chip OR text button OR opacity-0 placeholder).
// Pinned to the 30px TrendChip slot height so the header row is identical
// across all KPI columns regardless of what fills the right slot — a shorter
// element (e.g. the 20px Nudge button) would otherwise raise that column's
// stat value out of line with its neighbors.
const EYEBROW_HEIGHT = 30

function Eyebrow({ label, right }: { label: string; right: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', width: '100%', gap: 8, minHeight: EYEBROW_HEIGHT }}>
      <span style={{
        fontSize: 'var(--text-xs-size)', fontWeight: 'var(--text-xs-weight)', lineHeight: 'var(--text-xs-lh)', letterSpacing: '0.12px',
        color: 'var(--seafoam)', textTransform: 'uppercase', /* ⚠ uppercase xs-body, not caption (weight 400 not 600) */
        fontFamily: 'var(--font-body)',
      }}>
        {label}
      </span>
      {right}
    </div>
  )
}

// Recoleta H3 stat with optional dimmed suffix (e.g. "8" + "of 10").
function StatValue({ value, valueDimSuffix }: { value: string; valueDimSuffix?: string }) {
  return (
    <span style={{
      fontFamily: 'var(--font-heading)', fontWeight: 'var(--text-h3-weight)',
      fontSize: 'var(--text-h3-size)', lineHeight: 'var(--text-h3-lh)', color: 'var(--evergreen)',
    }}>
      {value}
      {valueDimSuffix && (
        <span style={{ color: 'var(--blue-grey)' }}> {valueDimSuffix}</span>
      )}
    </span>
  )
}

// Detail / supporting line — Figtree 14 Blue Grey. The label truncates so a
// `trailing` element (chevron, action chip) always stays fully visible in the
// narrow double-card columns. `flex-end` bottom-aligns the trailing element
// with the text's baseline instead of its vertical center. Pinned to the
// action-chip height for the same reason the eyebrow row is pinned: so a
// column carrying a chip is exactly as tall as one carrying only text.
const DETAIL_ROW_HEIGHT = 20

function DetailLine({ children, trailing }: { children: ReactNode; trailing?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', width: '100%', gap: 8, minHeight: DETAIL_ROW_HEIGHT }}>
      <span style={{
        fontSize: 'var(--text-sm-size)', fontWeight: 'var(--text-sm-weight)', lineHeight: 'var(--text-sm-lh)', letterSpacing: '0.14px',
        color: 'var(--blue-grey)', fontFamily: 'var(--font-body)',
        minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {children}
      </span>
      {trailing}
    </div>
  )
}

interface StatCardContentProps {
  eyebrow: string
  /** Omit to render only the eyebrow row (e.g. Gut Checks before a baseline
   *  exists — no number, detail line, or action to show yet). */
  value?: string
  valueDimSuffix?: string
  detail?: string
  trend?: Trend
  /** When truthy, renders a chevron-right at end of detail row (single tile only). */
  showChevron?: boolean
  /** Right-side text button in the eyebrow row (e.g. "Nudge"). */
  action?: { label: string; onClick?: () => void }
  /** Trailing element on the detail row (e.g. Gut Checks app-status pill). */
  statusIndicator?: ReactNode
}

// careOS "text-only" button → repo ui/Button `link` variant, with a feather
// resting background so it reads as a small chip — visually consistent with
// the status-tag pill it sits next to (e.g. Gut Checks' detail row). Hover
// goes solid aqua w/ white text, same as any other button of this kind.
function ActionButton({ action }: { action: { label: string; onClick?: () => void } }) {
  return (
    <Button
      variant="link"
      size="xs"
      onClick={action.onClick}
      bg="feather"
      px={3}
      py={1}
      _hover={{ bg: 'aqua', color: 'white' }}
    >
      {action.label}
    </Button>
  )
}

// Inner column body shared by single-tile and double-card columns.
function StatCardBody({ eyebrow, value, valueDimSuffix, detail, trend, showChevron, action, statusIndicator }: StatCardContentProps) {
  // A populated card (Gut Checks at baseline-complete) puts its status icon in
  // the eyebrow's right slot — the same 30px slot TrendChip fills — and drops
  // the action chip to the detail row. Keeping the icon out of the detail row
  // is what lets every column in the row resolve to the same height. Cards
  // with no value (pre-baseline Gut Checks) instead render their status pill
  // bottom-left, on its own (see below).
  const statusInEyebrow = value !== undefined && !!statusIndicator

  const headerRight = statusInEyebrow ? (
    statusIndicator
  ) : action ? (
    <ActionButton action={action} />
  ) : (
    <TrendChip trend={trend} />
  )

  const trailing = showChevron ? (
    <span style={{ display: 'inline-flex', color: 'var(--blue-grey)' }} aria-hidden>
      <ChevronRight />
    </span>
  ) : statusInEyebrow && action ? (
    <ActionButton action={action} />
  ) : undefined

  return (
    <>
      <Eyebrow label={eyebrow} right={headerRight} />
      {value !== undefined ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, width: '100%' }}>
          <StatValue value={value} valueDimSuffix={valueDimSuffix} />
          {detail !== undefined && (
            <DetailLine trailing={trailing}>{detail}</DetailLine>
          )}
        </div>
      ) : (
        // No baseline yet — nothing to show but the status pill itself.
        // `margin-top: auto` drops it to the column's bottom-left, so it lands
        // on the same 16px interior padding as a populated card's detail row.
        statusIndicator && (
          <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginTop: 'auto' }}>
            {statusIndicator}
          </div>
        )
      )}
    </>
  )
}

interface StatCardProps extends StatCardContentProps {
  onClick?: () => void
  /** Optional fixed width (Short variant 194 / Wide 260). Defaults to fluid. */
  width?: number | string
}

// Card-level CSS tokens shared between single and double variants.
const cardCss = {
  background: '{colors.canvas}',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: '{colors.cloud}',
  borderRadius: '{radii.card}',
  fontFamily: '{fonts.body}',
}

// Single tile (Symptom Change). Whole card is a click target when onClick is set.
// Height is content-driven (not fixed) so the 16px `--space-s` padding is
// exact on every edge; the row wrapping StatCard/StatCardDouble in
// PatientProfile stretches every card to match the tallest sibling, keeping
// heights consistent across the row without hardcoding a shared number here.
export function StatCard({ onClick, width, ...content }: StatCardProps) {
  const layoutStyle = {
    width: width ?? 'auto', flexShrink: 0,
    padding: 'var(--space-s)',
    display: 'flex', flexDirection: 'column' as const, justifyContent: 'flex-start' as const, gap: 'var(--space-xs)',
    alignItems: 'flex-start' as const,
    textAlign: 'left' as const,
  }
  if (onClick) {
    return (
      <Pressable type="button" onClick={onClick} css={cardCss} style={{ ...layoutStyle, cursor: 'pointer' }}>
        <StatCardBody {...content} showChevron />
      </Pressable>
    )
  }
  return (
    <chakra.div css={cardCss} style={layoutStyle}>
      <StatCardBody {...content} />
    </chakra.div>
  )
}

interface StatCardDoubleProps {
  left: StatCardContentProps
  right: StatCardContentProps
}

// Double card with Cloud vertical divider; flex-1 by default to fill remaining
// row width. Height is content-driven, same rationale as StatCard above —
// `align-items: stretch` (the flex default) still equalizes the two inner
// columns against each other and against a taller sibling card in the row.
export function StatCardDouble({ left, right }: StatCardDoubleProps) {
  return (
    <chakra.div
      css={{ ...cardCss, overflow: 'hidden' }}
      style={{ flex: 1, display: 'flex' }}
    >
      <div style={{
        flex: 1, minWidth: 0, padding: 'var(--space-s)',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', gap: 'var(--space-xs)',
        alignItems: 'flex-start',
        borderRight: '1px solid var(--cloud)',
      }}>
        <StatCardBody {...left} />
      </div>
      <div style={{
        flex: 1, minWidth: 0, padding: 'var(--space-s)',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', gap: 'var(--space-xs)',
        alignItems: 'flex-start',
      }}>
        <StatCardBody {...right} />
      </div>
    </chakra.div>
  )
}
